// app.service.ts

import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { OktaAuth } from '@okta/okta-auth-js'
import { environment } from '@environments/environment';
import {
  ServiceLibrary,
  ServiceReturn,
} from '@common_libraries/helpers/ServiceLibrary';
import { Observable, Subject } from 'rxjs';
import { Dictionary } from '@common_libraries/helpers/Dictionary';
import { shareReplay } from 'rxjs/operators';
import { LogoutReasons } from '@common_libraries/lib/Enums';
import { LocalStorageService } from 'ngx-webstorage';
import { SessionService } from '@services/session/session.service';
import { UserService } from '@services/user/user.service';

@Injectable({providedIn: 'root'})
export class OktaAuthService {
  CLIENT_ID = environment.Okta_CLIENT_ID;
  ISSUER = environment.Okta_ISSUER;
  LOGIN_REDIRECT_URI = environment.Okta_LOGIN_REDIRECT_URI;
  LOGOUT_REDIRECT_URI = environment.Okta_LOGOUT_REDIRECT_URI;

  oktaTokenKey: string = 'okta-accesstoken';
  oktaReady: Subject<boolean> = new Subject<boolean>();
  oktaReadyObservable = this.oktaReady.pipe(shareReplay(1));
  oktaSetReady: Subject<boolean> = new Subject<boolean>();
  oktaSetReadyObservable = this.oktaSetReady.pipe(shareReplay(1));
  oktaAuth: OktaAuth;

  constructor(
    private router: Router,
    private servLib: ServiceLibrary,
    private localSt: LocalStorageService,
    private sessionService: SessionService,
    public userService: UserService
  ) {
    this.oktaReadyObservable = this.oktaReady.pipe(shareReplay(1));
    //subscribe here because it seems unhappy if not
    this.oktaReadyObservable.subscribe((ready) => { });
    this.oktaSetReadyObservable.subscribe((ready) => { });

    this.oktaReady.next(false);
    this.oktaSetReady.next(false);


    this.expireLegacyTokenOnProxy().subscribe((res: any) => {
      if (res.json.toLowerCase() === "removed") {
        //Legacy auth cookie found, reloading 
        console.log("Legacy Cookie Found - removing");
        window.location.reload();
      }
      this.setupOktaAuth();
    });

  }

  async clearCodeProxy() {
    const parameters = new Dictionary([]);
    parameters.add('Key', this.oktaTokenKey);
    parameters.add('Value', '');

    await this.servLib.HttpPost(
      '/api/Session/Clear',
      ServiceLibrary.ContentType.JSON,
      parameters
    ).subscribe();

    return 'clear';
  }

  async setCodeProxy() {
    const accessToken = await this.oktaAuth.tokenManager.get('accessToken');
    const parameters = new Dictionary([]);
    parameters.add('Key', this.oktaTokenKey);
    parameters.add('Value', JSON.stringify(accessToken));

    await this.servLib.HttpPost(
      '/api/Session/Set',
      ServiceLibrary.ContentType.JSON,
      parameters
    ).subscribe((res)=>{
      this.oktaSetReady.next(true);
    });

    return 'set';
  }

  setupOktaAuth() {
    var finalURL = '';

    if (window.location.host.includes("localhost")) {
      finalURL = this.LOGIN_REDIRECT_URI;
    } else {
      var subdomain = window.location.host.split('.')[1] ? window.location.host.split('.')[0] : '';
      finalURL = this.LOGIN_REDIRECT_URI.replace("[login_redirect]", subdomain);
    }

    this.oktaAuth = new OktaAuth({
      clientId: this.CLIENT_ID,
      issuer: this.ISSUER,
      redirectUri: finalURL,
      pkce: true,
      tokenManager: {
        autoRenew: true,
        expireEarlySeconds: 30,
        secure: true,
        storage: 'cookie'
      },
      transformAuthState: async (oktaAuth, authState) => {
        if (!authState.isAuthenticated) {
          //not authenticated so clear any access token on proxy
          await this.clearCodeProxy();
          return authState;
        }

        const user = await oktaAuth.token.getUserInfo();
        authState.isAuthenticated = !!user;

        //authenticated so pass access token to proxy
        if (authState.isAuthenticated) {
          await this.setCodeProxy();
        }
        return authState;
      }
    });

    // From GITHUB doc- Fired before a token is set to expire (using expireEarlySeconds option, 30 seconds by default). 
    // If autoRenew option is set to true, a listener will be attached to this event and an attempt will
    // be made to renew the token when the event fires.
    this.oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
      console.log('Token with key', key, ' has expired:');
      console.log(expiredToken);
    });

    // From GITHUB doc- Fired when a token has been renewed by the tokenManager, either via the autoRenew process 
    // or as a result of calling tokenManager.renew
    this.oktaAuth.tokenManager.on('renewed', function (key, newToken, oldToken) {
      console.log('Token with key', key, 'has been renewed');
      console.log('Old token:', oldToken);
      console.log('New token:', newToken);
      root.setCodeProxy();
    });

    // From GITHUB doc- Fired when a token renew attempt has failed. 
    // This is a permanent error, and the token will be removed from storage.
    const root = this;
    this.oktaAuth.tokenManager.on('error', function (err) {
      console.log('TokenManager error:', err);
      //token renew attempt failed, so send them to login
      if (!window.location.href.includes('/login')) {
        root.sessionService.SetIsLoggedIn(false);
        window.location.href = '/login/' + LogoutReasons.OktaTokenRenewError;
      }
    });

    this.servLib.oktaAuth = this.oktaAuth;
    //set up authstate handlers
    this.oktaAuth.authStateManager.subscribe(authState => {
      if (authState !== undefined && authState !== null && authState.isAuthenticated) {
        // we are authenticated now, we can officially set okta as ready
      }
    });
    this.oktaAuth.authStateManager.updateAuthState();
    this.oktaAuth.start().then((result) => { });
    this.oktaReady.next(true);

  }

  expireLegacyTokenOnProxy() {
    const parameters = new Dictionary([]);

    return this.servLib.HttpPost(
      '/api/Session/ExpireLegacyCookie',
      ServiceLibrary.ContentType.JSON,
      parameters
    );
  }

  isAuthenticated(navUrl: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.oktaReadyObservable.subscribe(async (ready) => {
        if (ready) {
          let authState = this.oktaAuth.authStateManager.getAuthState();

          if (authState !== undefined && authState !== null && authState.isAuthenticated) {
            // we have a token, we should be safe to move on
            this.sessionService.SetIsLoggedIn(true);
            resolve(true);
          } else if (authState !== undefined && authState === null) {
            this.oktaAuth.authStateManager.subscribe(authState => {
              if (authState !== undefined && authState !== null && authState.isAuthenticated) {
                // we have a token, we should be safe to move on
                this.sessionService.SetIsLoggedIn(true);
                resolve(true);
              } else {
                // go to login
                reject(false);
                this.redirectToLogin(navUrl);
              }
            });
          } else {
            // go to login
            reject(false);
            this.redirectToLogin(navUrl);
          }

        }
      });
    });
  }

  loginOktaCheck(originalUrl, userName, passWord) {

    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);

    return new Promise((resolve, reject) => {
      // attempt to get user login flow
      const self = this;
      this.oktaAuth.webfinger({
        resource: 'acct:' + userName,
        rel: 'okta:idp'
      })
        .then((res: any) => {
          if (res.links !== undefined && res.links.length > 0 && res.links[0].properties['okta:idp:type'] === 'OKTA') {
            resolve({ directoryUser: true, userName: userName, idp: '' });
          } else if (res.links !== undefined && res.links.length > 0 && res.links[0].properties['okta:idp:id'] === environment.Okta_TigerRiskIDP) {
            resolve({ directoryUser: false, userName: userName.substring(0, userName.lastIndexOf('@')), idp: environment.Okta_TigerRiskIDP });
          } else {
            resolve({ directoryUser: false, userName: userName, idp: '' });
          }

        })
        .catch((err) => {
          //console.error(err);
          reject(false);
        });
    });

  }

  loginDirectoryUser(originalUrl, userName, passWord) {
    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);

    // performs the login and notifies ui what's happening
    return new Promise((resolve, reject) => {
      this.oktaAuth
        .signIn({
          username: userName,
          password: passWord,
        })
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  performOktaRedirect(res: any, userName) {
    this.oktaAuth.token.getWithRedirect({
      sessionToken: res.sessionToken,
      scopes: ['openid', 'email', 'profile'],
    });

  }

  performSAMLRedirect(userName, idp = '') {
    let redirectOptions = {
      redirect_uri: this.LOGIN_REDIRECT_URI,
      scopes: ['openid', 'email', 'profile'],
      loginHint: userName,
      display: userName
    };
    if (idp.length > 0) {
      redirectOptions['idp'] = idp;
    }
    this.oktaAuth.token.getWithRedirect(redirectOptions);
  }

  public systemLinkCheck(username: string) {
    const parent = this;
    return Observable.create((observer) => {
      parent.userService
        .SystemLink(username)
        .subscribe((res: any) => {
          observer.next(res.json.data);
          observer.complete();
        });
    });
  }

  async handleAuthentication(letOktaRedirect: boolean = true) {
    try {
      const res = await this.oktaAuth.token.parseFromUrl();

      // if (res.tokens.idToken) {
      //   await this.oktaAuth.tokenManager.add('idToken', res.tokens.idToken);
      // }
      // if (res.tokens.accessToken) {
      //   await this.oktaAuth.tokenManager.add(
      //     'accessToken',
      //     res.tokens.accessToken
      //   );
      // }
      //docs say to prefer this to not have false authstate changes across tabs
      if (res.tokens.idToken && res.tokens.accessToken) {
        await this.oktaAuth.tokenManager.setTokens(res.tokens);
      }

      // Retrieve the saved URL and navigate back
      if (letOktaRedirect) {
        const url = sessionStorage.getItem('okta-app-url');
        sessionStorage.removeItem('okta-app-url');
        this.router.navigateByUrl(url);
      }
    } catch (ex) {
      if (ex !== undefined && ex.errorCode !== undefined) {
        if (ex.errorCode === 'access_denied') {
          this.redirectToLogin();
        }
      }
    }

  }

  async logout() {
    var finalURL = '';

    if (window.location.host.includes("localhost")) {
      finalURL = this.LOGOUT_REDIRECT_URI;
    } else {
      var subdomain = window.location.host.split('.')[1] ? window.location.host.split('.')[0] : '';
      finalURL = this.LOGOUT_REDIRECT_URI.replace("[logout_redirect]", subdomain);
    }

    this.oktaReadyObservable.subscribe(async (ready) => {
      this.clearEventsAndTokenManager();
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: finalURL,
        revokeAccessToken: true
      });
    });

  }

  clearEventsAndTokenManager() {
    this.oktaAuth.tokenManager.clear();
  }

  redirectIfNoCodes() {
    if (
      !window.location.href.includes('/login') &&
      !window.location.href.includes('/callback') &&
      !window.location.href.includes('/verifypasswordreset') &&
      !window.location.href.includes('/privacyPolicy')
    ) {
      this.clearEventsAndTokenManager();
      window.location.href = '/login/' + LogoutReasons.OktaTokenExpiredNoCodes;
    }
  }

  redirectToLogin(overrideNavURL: string = '') {
    this.clearEventsAndTokenManager();

    if (
      !window.location.href.includes('/login') &&
      !window.location.href.includes('/callback') &&
      !window.location.href.includes('/verifypasswordreset') &&
      !window.location.href.includes('/privacyPolicy')
    ) {
      const snapshot: RouterStateSnapshot = this.router.routerState.snapshot;

      this.localSt.store('redirect_url_create', Date.now());

      let goToURL = '';

      if (overrideNavURL.length > 0) {
        goToURL = overrideNavURL;
      } else {
        goToURL = snapshot.url;
        if (goToURL === '') {
          goToURL = window.location.pathname;
        }
      }
      this.localSt.store('redirect_url', goToURL);
    }

    this.sessionService.SetIsLoggedIn(false);
    if (window.location.pathname !== '/') {
      window.location.href = '/login/' + LogoutReasons.OktaTokenExpired;
    } else {
      window.location.href = '/login';
    }
  }
}
